import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useListings } from '@/hooks'
import { ListingCard } from './ListingCard'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

interface RelatedListingsProps {
  currentListingId: string
  category: string
  sellerId: string
  limit?: number
}

export function RelatedListings({
  currentListingId,
  category,
  sellerId,
  limit = 4,
}: RelatedListingsProps) {
  const { listings, isLoading } = useListings({ category })

  const relatedListings = useMemo(() => {
    return listings
      .filter((listing) => listing.id !== currentListingId)
      .slice(0, limit)
  }, [listings, currentListingId, limit])

  const sellerListings = useMemo(() => {
    return listings
      .filter((listing) => listing.id !== currentListingId && listing.sellerId === sellerId)
      .slice(0, 4)
  }, [listings, currentListingId, sellerId])

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    )
  }

  if (relatedListings.length === 0 && sellerListings.length === 0) {
    return null
  }

  return (
    <div className="space-y-12">
      {/* Seller's other listings */}
      {sellerListings.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">More from this Seller</h2>
            <Link
              to={`/browse?sellerId=${sellerId}`}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {sellerListings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} compact />
            ))}
          </div>
        </section>
      )}

      {/* Related listings in same category */}
      {relatedListings.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Similar Listings</h2>
            <Link
              to={`/browse?category=${category}`}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {relatedListings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} compact />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
